import { inject, provide, readonly, ref, type InjectionKey, type Ref } from 'vue';
import type { Checkout } from '@vp/common';

type Blueprint = Checkout.TicketBlueprint.CheckoutTicketBlueprint;

/**
 * The channel a seated event page's ticket rows and its seating picker talk over.
 *
 * They are siblings — `EventDetails` renders both, and `SeatsioPicker` is behind
 * `clientOnly()`, so a template ref never reaches it. `EventDetails` is the only common
 * ancestor, so it provides this and both children inject it: a row asks for best available
 * seats for its own tier, and the picker (which owns the seats.io chart) answers.
 */
export type BestAvailableSeats = {
  /** The tier whose modal is open, if any. */
  requested: Readonly<Ref<Blueprint | undefined>>;
  /** Whether a live chart exists to hold seats on. */
  chartReady: Readonly<Ref<boolean>>;
  /**
   * The seats best available obtained, as `selectedKey` builds them. A row reads it to decide
   * whether to say so instead of offering to find them again.
   *
   * Deliberately not the picker's own `heldByBestAvailable` set: that one is chart-scoped and
   * `releaseChart()` empties it on every rebuild — which is exactly the re-render this has to
   * survive. It lives here because the channel is provided by `EventDetails`, which outlives
   * every chart the picker builds.
   *
   * It does not outlive the *page*, and deliberately so. A reload restores the cart but not
   * this, so the row offers its button again for a tier whose best-available seats are still
   * carted. Persisting it would mean either reading storage in `setup()` — which the server
   * cannot do, so the chip would hydrate as a mismatch — or flipping the row a tick after mount,
   * which is a visible flash for a state nothing acts on. The reloaded row is benign either way:
   * the seats are visible in the cart and selected on the chart, and a second run accumulates
   * rather than displacing them.
   */
  selectedSeats: Readonly<Ref<Set<string>>>;
  markSelected: (blueprintId: number, labels: string[]) => void;
  forgetSelected: (blueprintId: number, labels: string[]) => void;
  clearSelected: () => void;
  request: (ticket: Blueprint) => void;
  cancel: () => void;
  setChartReady: (ready: boolean) => void;
  registerRows: (el: HTMLElement | undefined) => void;
  scrollToRows: () => void;
};

/**
 * One flat set of composite keys rather than a per-tier map of labels, for two reasons.
 *
 * A map keyed by tier invites a whole-array write, and a second best-available run on the same
 * tier would then displace the first rather than accumulate.
 *
 * And the blueprint belongs in the key, not just the value: a GA area can be carted under two
 * blueprints at once, and seats.io fires `objectDeselected` per ticket type — so a partial
 * deselect for one tier must not take the other tier's seats with it.
 */
export const selectedKey = (blueprintId: number, label: string): string => `${blueprintId}:${label}`;

export const bestAvailableSeatsKey: InjectionKey<BestAvailableSeats> = Symbol('vp-best-available-seats');

/**
 * Construction is separate from `provide()` so a spec can build a channel outside a host
 * component without tripping Vue's "provide() can only be used inside setup()" warning.
 */
export const createBestAvailableSeats = (): BestAvailableSeats => {
  const requested = ref<Blueprint | undefined>(undefined);
  const chartReady = ref(false);
  // `ref` proxies a Set's collection methods, so `add`/`delete`/`clear` on the raw ref notify
  // and a `has` read inside a consumer's computed tracks.
  const selectedSeats = ref(new Set<string>());
  // Deliberately not reactive: nothing renders from it, and making a DOM node reactive
  // would proxy it — `scrollIntoView` would then run against the proxy, not the element.
  let rowsEl: HTMLElement | undefined;

  return {
    requested: readonly(requested) as Readonly<Ref<Blueprint | undefined>>,
    chartReady: readonly(chartReady),
    selectedSeats: readonly(selectedSeats) as Readonly<Ref<Set<string>>>,
    markSelected: (blueprintId: number, labels: string[]) => {
      labels.forEach(label => selectedSeats.value.add(selectedKey(blueprintId, label)));
    },
    forgetSelected: (blueprintId: number, labels: string[]) => {
      labels.forEach(label => selectedSeats.value.delete(selectedKey(blueprintId, label)));
    },
    clearSelected: () => {
      selectedSeats.value.clear();
    },
    request: (ticket: Blueprint) => {
      requested.value = ticket;
    },
    cancel: () => {
      requested.value = undefined;
    },
    setChartReady: (ready: boolean) => {
      chartReady.value = ready;
    },
    registerRows: (el: HTMLElement | undefined) => {
      rowsEl = el;
    },
    scrollToRows: () => {
      rowsEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  };
};

export const provideBestAvailableSeats = (): BestAvailableSeats => {
  const api = createBestAvailableSeats();
  provide(bestAvailableSeatsKey, api);

  return api;
};

/**
 * Undefined on an unseated page and in an isolated mount — no ancestor provided a channel.
 * Callers render no best-available affordance at all in that case.
 */
export const useBestAvailableSeats = (): BestAvailableSeats | undefined =>
  inject(bestAvailableSeatsKey, undefined);
